import React from 'react';
import { Search, GripVertical, SlidersHorizontal, X } from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import { useDraggable } from '@dnd-kit/core';
import { offeringPositionsApiBase } from '../../../utils/offeringApi';
import {
  fetchPickerItems,
  mergePickerResults,
  pickerIdentityLabel,
  pickerPricingLabel,
  quickSearchPickerItems,
  sortPickerItemsByName,
  type PickerItem,
  type PickerItemKind,
  type PickerResult,
} from '../utils/offerPositionPickerData';
import { OfferPositionAdvancedSearchDialog, PickerKindBadge } from './OfferPositionAdvancedSearchDialog';

/** Below this length the sidebar shows the default list instead of searching. */
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const fmtCount = (n: number) => Number(n || 0).toLocaleString('de-DE');

const EMPTY_RESULT: PickerResult = { items: [], total: 0 };

/** Stable key across both kinds — material and Leistung ids may collide. */
export const pickerItemKey = (item: Pick<PickerItem, 'kind' | 'id'>) => `${item.kind}:${item.id}`;

export type OfferPositionPickerPanelProps = {
  leadRouteId?: string;
  offeringId?: string | null;
  /** Override API base (e.g. `/api/offering-vorlagen/:id/positions`). */
  positionsApiBase?: string;
  onAdded: () => void | Promise<void>;
  onInsertItem?: (item: {
    kind: 'material' | 'leistung';
    id: string;
    insertIndex?: number;
    name?: string;
    unit?: string | null;
    gross_price?: number | null;
    description?: string | null;
  }) => void | Promise<unknown>;
  dragAndDropEnabled?: boolean;
};

export function OfferPositionPickerPanel({
  leadRouteId = '',
  offeringId,
  positionsApiBase: positionsApiBaseProp,
  onAdded,
  onInsertItem,
  dragAndDropEnabled = false,
}: OfferPositionPickerPanelProps) {
  const [searchInput, setSearchInput] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [addingKey, setAddingKey] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<Record<PickerItemKind, PickerResult>>({
    material: EMPTY_RESULT,
    leistung: EMPTY_RESULT,
  });
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  // Search as you type — no button; short input falls back to the default list.
  React.useEffect(() => {
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_QUERY_LENGTH ? trimmed : '';
    if (next === query) return;
    const t = window.setTimeout(() => setQuery(next), next ? SEARCH_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(t);
  }, [searchInput, query]);

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const load = (kind: PickerItemKind) =>
      query
        ? quickSearchPickerItems(kind, query, controller.signal)
        : fetchPickerItems({ kind, query: '', mode: 'keyword', signal: controller.signal });
    Promise.all([load('leistung'), load('material')])
      .then(([leistung, material]) => {
        if (!controller.signal.aborted) setResults({ leistung, material });
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(e instanceof Error && e.message ? e.message : 'Einträge konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  const baseUrl = React.useMemo(
    () =>
      positionsApiBaseProp ??
      (offeringId
        ? offeringPositionsApiBase(offeringId)
        : `/api/leads/${encodeURIComponent(leadRouteId)}/positions`),
    [positionsApiBaseProp, offeringId, leadRouteId],
  );

  /** Inserts one item (sidebar click or „Erweiterte Suche“); throws so callers can show the error. */
  const insertItem = React.useCallback(
    async (item: PickerItem) => {
      if (onInsertItem) {
        await Promise.resolve(
          onInsertItem({
            kind: item.kind,
            id: item.id,
            name: item.name,
            unit: item.unit,
            gross_price: item.gross_price,
            description: item.description,
          }),
        );
        return;
      }
      const body = item.kind === 'material' ? { material_id: item.id } : { leistung_id: item.id };
      const res = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'Hinzufügen fehlgeschlagen');
      }
      await onAdded();
    },
    [baseUrl, onAdded, onInsertItem],
  );

  const handlePick = async (item: PickerItem) => {
    setAddingKey(pickerItemKey(item));
    setError(null);
    try {
      await insertItem(item);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setAddingKey(null);
    }
  };

  // One list: best match first while searching, alphabetical otherwise.
  const items = React.useMemo(
    () =>
      query
        ? mergePickerResults([results.leistung.items, results.material.items], query)
        : sortPickerItemsByName([...results.leistung.items, ...results.material.items]),
    [query, results],
  );
  const pendingQuery = searchInput.trim().length >= MIN_QUERY_LENGTH && searchInput.trim() !== query;

  return (
    <div className="flex flex-col min-h-0 flex-1 w-full overflow-hidden">
      <div className="p-3 space-y-3 overflow-y-auto flex-1 min-h-0 flex flex-col">
        <div className="flex gap-2 shrink-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && searchInput) {
                  e.preventDefault();
                  setSearchInput('');
                }
              }}
              placeholder="Material oder Leistung suchen…"
              aria-label="Materialien und Leistungen durchsuchen"
              className="w-full pl-8 pr-7 py-2 text-xs border border-app-border rounded-lg bg-app-surface focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue"
              autoComplete="off"
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-app-hover hover:text-app-text"
                aria-label="Suche leeren"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setAdvancedOpen(true)}
            className="shrink-0 inline-flex items-center justify-center rounded-lg border border-app-border bg-app-surface px-2.5 text-app-text-muted hover:bg-app-hover hover:text-app-text focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
            title="Erweiterte Suche mit Filtern und Tabellenansicht"
            aria-label="Erweiterte Suche öffnen"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
          </button>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-800 shrink-0">
            {error}
          </div>
        ) : null}

        <div className="flex-1 min-h-[72px] md:min-h-[120px] flex flex-col border border-app-border rounded-lg bg-app-surface overflow-hidden">
          <p className="flex items-center justify-between gap-2 text-[11px] font-medium text-app-text-subtle px-2.5 py-1.5 border-b border-app-border bg-app-surface-muted/80 shrink-0">
            <span>{query ? `${fmtCount(items.length)} Treffer für „${query}“` : 'Materialien & Leistungen'}</span>
            {pendingQuery ? <LoadingSpinner size="sm" className="text-app-text-subtle" /> : null}
          </p>
          {loading ? (
            <InlineLoadingState label="Laden…" className="flex-1 py-8 text-xs" />
          ) : items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center text-xs text-app-text-subtle px-3 py-6">
              <span>{query ? `Keine Treffer für „${query}“.` : 'Noch keine Materialien oder Leistungen.'}</span>
              {query ? (
                <button
                  type="button"
                  onClick={() => setAdvancedOpen(true)}
                  className="font-medium text-pipedrive-blue hover:underline"
                >
                  In der erweiterten Suche weitersuchen
                </button>
              ) : null}
            </div>
          ) : (
            <ul className="overflow-y-auto flex-1 divide-y divide-app-border">
              {items.map((item) => (
                <li key={pickerItemKey(item)}>
                  <PickerListItem
                    item={item}
                    busy={addingKey === pickerItemKey(item)}
                    dragAndDropEnabled={dragAndDropEnabled}
                    onPick={() => void handlePick(item)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <OfferPositionAdvancedSearchDialog
        open={advancedOpen}
        initialQuery={searchInput.trim()}
        onClose={() => setAdvancedOpen(false)}
        onPick={insertItem}
      />
    </div>
  );
}

function PickerListItem({
  item,
  busy,
  dragAndDropEnabled,
  onPick,
}: {
  item: PickerItem;
  busy: boolean;
  dragAndDropEnabled: boolean;
  onPick: () => void;
}) {
  const meta = [pickerIdentityLabel(item), pickerPricingLabel(item)].filter(Boolean).join(' · ');
  return (
    <PickerItemDraggable
      id={`picker-${item.kind}-${item.id}`}
      enabled={dragAndDropEnabled}
      data={{
        source: 'picker',
        kind: item.kind,
        id: item.id,
        name: item.name,
        unit: item.unit,
        gross_price: item.gross_price,
        description: item.description,
      }}
    >
      <button
        type="button"
        disabled={busy}
        onClick={onPick}
        className={`w-full text-left px-2.5 py-2 hover:bg-app-hover disabled:opacity-50 disabled:pointer-events-none transition-colors ${
          dragAndDropEnabled ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 flex items-start gap-1.5">
            <GripVertical className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
            <span className="text-xs font-medium text-app-text leading-snug line-clamp-2" title={item.name}>
              {item.name}
            </span>
          </span>
          {busy ? <LoadingSpinner size="sm" className="shrink-0 mt-0.5 text-app-text-subtle" /> : null}
        </div>
        {/* Typ, Art.-Nr./Herkunft, Einheit und Preise in einer Zeile. */}
        <p className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-app-text-muted">
          <PickerKindBadge kind={item.kind} />
          {meta ? (
            <span className="truncate tabular-nums" title={meta}>
              {meta}
            </span>
          ) : null}
        </p>
      </button>
    </PickerItemDraggable>
  );
}

function PickerItemDraggable({
  id,
  data,
  enabled,
  children,
}: {
  id: string;
  data: Record<string, unknown>;
  enabled: boolean;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data,
    disabled: !enabled,
  });
  const style: React.CSSProperties | undefined = isDragging ? { opacity: 0 } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={enabled ? 'touch-none' : undefined}>
      {children}
    </div>
  );
}
